import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Popconfirm, Table, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck, faPlus, faRotateRight, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  useDeleteExternalMutation,
  useLazyGetExternalDetailsQuery,
  useLazyGetExternalEndpointsQuery,
  usePutExternalRoleMutation,
} from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import { Permission, hasPermission } from "../../../types/permissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
import type { RootState } from "../../../app/store";
import type {
  ExternalDetail,
  ExternalMutationResult,
} from "../../../interfaces/External.interface";
import ApiClientRoleSelect from "./ApiClientRoleSelect";
import ApiClientCreateModal from "./ApiClientCreateModal";
import ApiClientSecretModal from "./ApiClientSecretModal";
import ApiClientGrantsModal, { type SavedGrants } from "./ApiClientGrantsModal";
import { useApiClientRoles } from "./apiClientRoles";
import apiClientErrorMessage from "./apiClientErrors";

/**
 * The API clients (LiftCloud Externals) of the selected organization: the
 * credentials another system uses on the external API, and the role each one
 * acts as there (LIFT-1098).
 *
 * The organization is the header's selection, falling back to the signed-in
 * user's own. Listing needs externals-view (the screen permission); the
 * create, role and delete controls each appear only with their own
 * permission, and LiftCloud checks all of them again. Granting endpoints
 * also needs external-endpoints-view, to read the catalog granted from.
 *
 * LiftCloud does not report which endpoints a client holds (no Externals read
 * path carries grants), so the Endpoint access column shows what this page
 * granted since it was opened, or "Not reported". A client with no grant is
 * refused on every /api/ext route, which the warning says.
 */
function ApiClientsContent() {
  const [t] = useTranslation("global");
  const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const held = permissionSet ?? [];
  const canCreate = hasPermission(Permission.ExternalsCreate, held);
  const canUpdate = hasPermission(Permission.ExternalsUpdate, held);
  const canDelete = hasPermission(Permission.ExternalsDelete, held);
  const canViewEndpoints = hasPermission(Permission.ExternalEndpointsView, held);
  const canGrant = canViewEndpoints && (canCreate || canUpdate);

  const orgId: string = globalOrganizationId || userLogged?.organizationId || "";

  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<ExternalMutationResult | null>(null);
  const [savingRoleFor, setSavingRoleFor] = useState("");
  const [editingGrants, setEditingGrants] = useState<ExternalDetail | null>(null);
  // Grants this page set, by client id: the only grants the page can know
  // while LiftCloud reports none.
  const [grantedHere, setGrantedHere] = useState<Record<string, string[]>>({});
  // Set the moment a catalog read is asked for and cleared when the last one
  // settles, so save is held back while the token is still being fetched,
  // before the query itself reports isFetching. Reads overlap (the mount
  // read, then a dialog's), hence the count.
  const catalogReads = useRef(0);
  // Only the latest read decides whether the token failed.
  const latestCatalogRead = useRef(0);
  const [catalogRequested, setCatalogRequested] = useState(false);
  const [tokenProblem, setTokenProblem] = useState("");

  const [triggerList, { data, isFetching, error: listError }] = useLazyGetExternalDetailsQuery();
  const [putExternalRole] = usePutExternalRoleMutation();
  const [deleteExternal] = useDeleteExternalMutation();
  const { roles, isFetching: rolesLoading } = useApiClientRoles(orgId);
  const [
    triggerEndpoints,
    { data: catalog, isFetching: catalogFetching, isUninitialized, error: endpointsError },
  ] = useLazyGetExternalEndpointsQuery();
  const endpoints = catalog?.endpoints ?? [];
  let catalogProblem = "";
  if (tokenProblem) {
    catalogProblem = t("apiClients.catalogFailed", { reason: tokenProblem });
  } else if (endpointsError) {
    catalogProblem = t("apiClients.catalogFailed", {
      reason: apiClientErrorMessage(endpointsError, t("apiClients.genericError")),
    });
  } else if (catalog && !catalog.complete) {
    catalogProblem = t("apiClients.catalogIncomplete");
  }
  // Not yet asked counts as loading, so a dialog opened at once cannot save
  // against an empty catalog. A re-read keeps the list on screen and only
  // holds the dialogs' save back.
  const endpointsRefreshing = catalogFetching || catalogRequested;
  const endpointsLoading =
    !catalog && catalogProblem === "" && (endpointsRefreshing || (canGrant && isUninitialized));

  const load = async () => {
    if (!orgId) return;
    try {
      triggerList({ token: await GETJwtToken(), orgId });
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    }
  };

  useEffect(() => {
    load();
  }, [orgId]);

  // Read again whenever a dialog opens, so a save never names an entry
  // deleted since the page loaded (LiftCloud answers that with a 500).
  const loadEndpoints = async () => {
    if (!canGrant) return;
    catalogReads.current += 1;
    latestCatalogRead.current += 1;
    const read = latestCatalogRead.current;
    setCatalogRequested(true);
    try {
      const token = await GETJwtToken();
      if (read === latestCatalogRead.current) setTokenProblem("");
      await triggerEndpoints({ token });
    } catch (err) {
      if (read === latestCatalogRead.current) {
        setTokenProblem(apiClientErrorMessage(err, t("apiClients.genericError")));
      }
    } finally {
      catalogReads.current -= 1;
      if (catalogReads.current === 0) setCatalogRequested(false);
    }
  };

  useEffect(() => {
    loadEndpoints();
  }, [canGrant]);

  /** Opens the one-time secret dialog when a PUT issued a missing OAuth client. */
  const showIssuedSecret = (result: ExternalMutationResult | undefined) => {
    if (result?.client_secret) setCreated(result);
  };

  /** The client's grants as LiftCloud reported them, else as this page set them. */
  const knownGrants = (row: ExternalDetail): string[] | undefined =>
    row.permission_ids ?? grantedHere[row.id];

  const rememberGrants = (id: string, ids: string[]) =>
    setGrantedHere((previous) => ({ ...previous, [id]: ids }));

  /** Records what a grants save left the client holding, when that is known. */
  const rememberSaved = (id: string, { ids, exact }: SavedGrants) => {
    if (exact) {
      rememberGrants(id, ids);
      return;
    }
    // Added to: known only if what it held before was.
    setGrantedHere((previous) =>
      previous[id]
        ? { ...previous, [id]: Array.from(new Set([...previous[id], ...ids])) }
        : previous,
    );
  };

  useEffect(() => {
    setError(listError ? apiClientErrorMessage(listError, t("apiClients.genericError")) : "");
  }, [listError]);

  const changeRole = async (row: ExternalDetail, roleId: string) => {
    setError("");
    setSavingRoleFor(row.id);
    try {
      const token = await GETJwtToken();
      const result = await putExternalRole({ token, id: row.id, name: row.name, roleId }).unwrap();
      message.success(t("apiClients.roleUpdated"));
      showIssuedSecret(result);
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    } finally {
      setSavingRoleFor("");
    }
  };

  const remove = async (row: ExternalDetail) => {
    setError("");
    try {
      const token = await GETJwtToken();
      await deleteExternal({ token, id: row.id }).unwrap();
      message.success(t("apiClients.deleted"));
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    }
  };

  const columns = [
    {
      title: t("apiClients.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("apiClients.clientId"),
      dataIndex: "client_id",
      key: "client_id",
      render: (clientId: string) => <code>{clientId}</code>,
    },
    {
      title: t("apiClients.role"),
      key: "role",
      render: (_: unknown, row: ExternalDetail) =>
        canUpdate && row.org_id ? (
          <ApiClientRoleSelect
            testId={`api-client-role-${row.id}`}
            roles={roles}
            value={row.role_id}
            loading={rolesLoading || savingRoleFor === row.id}
            disabled={savingRoleFor === row.id}
            onChange={(roleId) => changeRole(row, roleId)}
          />
        ) : (
          <span data-testid={`api-client-role-name-${row.id}`}>
            {row.role_name ?? row.role_id ?? t("apiClients.noRole")}
          </span>
        ),
    },
    {
      title: t("apiClients.endpoints"),
      key: "endpoints",
      render: (_: unknown, row: ExternalDetail) => {
        const grants = knownGrants(row);
        let summary: React.ReactNode;
        if (grants === undefined) {
          summary = (
            <Typography.Text type="secondary">{t("apiClients.grantsUnknown")}</Typography.Text>
          );
        } else if (grants.length === 0) {
          summary = <Alert type="warning" showIcon message={t("apiClients.noGrantsWarning")} />;
        } else {
          summary = <span>{t("apiClients.grantCount", { count: grants.length })}</span>;
        }
        return (
          <div data-testid={`api-client-endpoints-${row.id}`}>
            {summary}
            {canUpdate && canViewEndpoints && row.org_id && (
              <Button
                size="small"
                className="mt-1"
                data-testid={`api-client-edit-endpoints-${row.id}`}
                icon={<FontAwesomeIcon icon={faListCheck} className="generalStyles__mrFix" />}
                onClick={() => {
                  loadEndpoints();
                  setEditingGrants(row);
                }}
              >
                {t("apiClients.editEndpoints")}
              </Button>
            )}
          </div>
        );
      },
    },
    ...(canDelete
      ? [
          {
            title: t("apiClients.actions"),
            key: "actions",
            render: (_: unknown, row: ExternalDetail) => (
              <Popconfirm
                title={t("apiClients.deleteConfirm", { name: row.name })}
                okText={t("apiClients.delete")}
                cancelText={t("apiClients.cancel")}
                okButtonProps={
                  { danger: true, "data-testid": `api-client-delete-confirm-${row.id}` } as Record<
                    string,
                    unknown
                  >
                }
                onConfirm={() => remove(row)}
              >
                <Button
                  danger
                  data-testid={`api-client-delete-${row.id}`}
                  icon={<FontAwesomeIcon icon={faTrash} className="generalStyles__mrFix" />}
                >
                  {t("apiClients.delete")}
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={`layout__contentPage__${theme}`} data-testid="api-clients-page">
      <div className="generalStyles__pageHeader">
        {canCreate && orgId && (
          <div className="generalStyles__mlFix mt-1">
            <Button
              type="primary"
              data-testid="api-client-create"
              icon={<FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />}
              onClick={() => {
                loadEndpoints();
                setCreateOpen(true);
              }}
            >
              {t("apiClients.create")}
            </Button>
          </div>
        )}
        <div className="generalStyles__mlFix mt-1">
          <Button
            data-testid="api-client-refresh"
            icon={<FontAwesomeIcon icon={faRotateRight} className="generalStyles__mrFix" />}
            onClick={load}
          >
            {t("apiClients.refresh")}
          </Button>
        </div>
      </div>
      <p className="mt-3">{t("apiClients.description")}</p>
      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError("")}
          className="mb-3"
          data-testid="api-clients-error"
        />
      )}
      {orgId ? (
        <Table
          data-testid="api-clients-table"
          rowKey="id"
          loading={isFetching}
          dataSource={data ?? []}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t("apiClients.empty") }}
          scroll={{ x: 900 }}
        />
      ) : (
        <Alert type="info" showIcon message={t("apiClients.noOrganization")} />
      )}
      <ApiClientCreateModal
        open={createOpen}
        orgId={orgId}
        roles={roles}
        rolesLoading={rolesLoading}
        endpoints={canViewEndpoints ? endpoints : null}
        endpointsLoading={endpointsLoading}
        endpointsRefreshing={endpointsRefreshing}
        catalogProblem={catalogProblem}
        onClose={() => setCreateOpen(false)}
        onCreated={(result, granted) => {
          setCreateOpen(false);
          setCreated(result);
          // A new client holds exactly what it was created with: nothing,
          // when no catalog could be used.
          if (canViewEndpoints) rememberGrants(result.id, granted ?? []);
        }}
      />
      <ApiClientGrantsModal
        key={editingGrants?.id ?? "closed"}
        client={editingGrants}
        current={editingGrants?.permission_ids ?? null}
        initial={editingGrants ? (knownGrants(editingGrants) ?? []) : []}
        endpoints={endpoints}
        endpointsLoading={endpointsLoading}
        endpointsRefreshing={endpointsRefreshing}
        catalogProblem={catalogProblem}
        onClose={() => setEditingGrants(null)}
        onSaved={(client, saved, result) => {
          setEditingGrants(null);
          rememberSaved(client.id, saved);
          message.success(t("apiClients.grantsUpdated"));
          showIssuedSecret(result);
        }}
      />
      <ApiClientSecretModal created={created} onClose={() => setCreated(null)} />
    </div>
  );
}

function ApiClientsPage() {
  // SCREEN_PERMISSION["api-clients"], the declaration the sidebar link reads.
  const allowed = useScreenPermission("api-clients");
  return allowed ? <ApiClientsContent /> : <UnauthorizedPage />;
}

export default ApiClientsPage;
